<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import OverflowMenu from '@/components/shared/OverflowMenu.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import { useTeamMembers } from '@/composables/useTeamMembers'
import type { TeamMember } from '@/types/team'

const props = defineProps<{
  organisationId: number
  currentUserId: number
  isOwner: boolean
}>()

const emit = defineEmits<{
  'role-changed': [userId: number, newRole: 'owner' | 'admin']
  'member-removed': [userId: number]
}>()

const { t } = useI18n()
const { members, loading, saving, errors, generalError, fetchMembers, updateMemberRole, removeMember } =
  useTeamMembers()

const confirmingRemoveUserId = ref<number | null>(null)
const pendingRoleChange = ref<{ userId: number; newRole: 'owner' | 'admin' } | null>(null)

function initials(member: TeamMember): string {
  return `${member.first_name.charAt(0)}${member.last_name.charAt(0)}`.toUpperCase()
}

function formatDate(dateString: string | null): string {
  if (!dateString) return '—'
  return new Date(dateString).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function isSelf(member: TeamMember): boolean {
  return member.id === props.currentUserId
}

function canShowRoleDropdown(member: TeamMember): boolean {
  if (!props.isOwner) return false
  // Do not show role dropdown for other owners
  if (member.role === 'owner' && member.id !== props.currentUserId) return false
  return true
}

function canShowRemoveButton(member: TeamMember): boolean {
  // Current user can always remove themselves
  if (isSelf(member)) return true
  // Owner can remove any non-owner
  if (props.isOwner && member.role !== 'owner') return true
  return false
}

async function handleRoleChange(member: TeamMember, newRole: 'owner' | 'admin'): Promise<void> {
  if (isSelf(member) && member.role === 'owner' && newRole === 'admin') {
    // Confirm self-demotion
    pendingRoleChange.value = { userId: member.id, newRole }
    return
  }
  await applyRoleChange(member.id, newRole)
}

async function confirmRoleChange(): Promise<void> {
  if (!pendingRoleChange.value) return
  const { userId, newRole } = pendingRoleChange.value
  pendingRoleChange.value = null
  await applyRoleChange(userId, newRole)
}

async function applyRoleChange(userId: number, newRole: 'owner' | 'admin'): Promise<void> {
  const updated = await updateMemberRole(props.organisationId, userId, newRole)
  if (updated) {
    emit('role-changed', userId, newRole)
  }
}

// Open row menus keyed by member id, so a menu can be closed once an item is chosen.
const menuRefs = new Map<number, { close: () => void }>()

function setMenuRef(id: number, el: unknown): void {
  if (el && typeof el === 'object' && 'close' in el) {
    menuRefs.set(id, el as { close: () => void })
  } else {
    menuRefs.delete(id)
  }
}

function requestRemove(userId: number): void {
  menuRefs.get(userId)?.close()
  confirmingRemoveUserId.value = userId
}

function cancelRemove(open: boolean): void {
  if (!open) confirmingRemoveUserId.value = null
}

async function confirmRemove(): Promise<void> {
  if (confirmingRemoveUserId.value === null) return
  const userId = confirmingRemoveUserId.value
  confirmingRemoveUserId.value = null
  const success = await removeMember(props.organisationId, userId)
  if (success) {
    emit('member-removed', userId)
  }
}

const roleBadgeClass = computed(() => (role: 'owner' | 'admin') =>
  role === 'owner'
    ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
    : 'bg-slate-500/20 text-slate-400 border border-slate-500/30',
)

onMounted(() => {
  fetchMembers(props.organisationId)
})
</script>

<template>
  <div>
    <!-- General error -->
    <div
      v-if="generalError"
      class="mb-4 rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-400"
    >
      {{ t(generalError) }}
    </div>

    <!-- Role change errors -->
    <div
      v-if="errors['role']"
      class="mb-4 rounded-lg bg-amber-500/10 border border-amber-500/30 px-4 py-3 text-sm text-amber-400"
    >
      {{ errors['role'][0] }}
    </div>

    <!-- Loading skeleton -->
    <div v-if="loading" class="space-y-3">
      <div
        v-for="i in 3"
        :key="i"
        class="h-14 rounded-lg bg-slate-700/50 animate-pulse"
      />
    </div>

    <!-- Empty state (defensive) -->
    <div
      v-else-if="members.length === 0"
      class="rounded-xl border border-dashed border-slate-700 px-8 py-12 text-center"
    >
      <p class="text-slate-400 text-sm">{{ t('team.members.empty_state') }}</p>
    </div>

    <!-- Members table -->
    <div v-else class="overflow-x-auto">
    <table class="w-full text-sm text-left">
      <thead>
        <tr class="border-b border-slate-700">
          <th class="px-3 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
            {{ t('team.members.column_member') }}
          </th>
          <th class="px-3 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
            {{ t('team.members.column_role') }}
          </th>
          <th class="px-3 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
            {{ t('team.members.column_joined') }}
          </th>
          <th v-if="isOwner" class="px-3 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">
            {{ t('team.members.column_actions') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="member in members"
          :key="member.id"
          class="group border-b border-slate-700/50 hover:bg-slate-800/50 transition-colors"
          :class="isSelf(member) ? 'bg-slate-800/30' : ''"
        >
          <!-- Avatar + name + email -->
          <td class="px-3 py-3">
            <div class="flex items-center gap-3">
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-xs font-semibold"
                :class="member.role === 'owner' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600 text-slate-300'"
              >
                {{ initials(member) }}
              </div>
              <div class="min-w-0">
                <div class="flex items-center gap-2">
                  <span class="font-medium text-white truncate">
                    {{ member.first_name }} {{ member.last_name }}
                  </span>
                  <span
                    v-if="isSelf(member)"
                    class="text-xs text-slate-400 font-normal"
                  >
                    {{ t('team.members.you_label') }}
                  </span>
                </div>
                <div class="text-xs text-slate-400 truncate">{{ member.email }}</div>
              </div>
            </div>
          </td>

          <!-- Role badge / dropdown -->
          <td class="px-3 py-3">
            <select
              v-if="canShowRoleDropdown(member)"
              :value="member.role"
              :disabled="saving"
              class="rounded-md border border-slate-600 bg-slate-900 text-sm text-white px-2 py-1 focus:outline-none focus:ring-1 focus:ring-emerald-500 disabled:opacity-50"
              @change="handleRoleChange(member, ($event.target as HTMLSelectElement).value as 'owner' | 'admin')"
            >
              <option value="admin">{{ t('team.roles.admin') }}</option>
              <option value="owner">{{ t('team.roles.owner') }}</option>
            </select>
            <span
              v-else
              class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
              :class="roleBadgeClass(member.role)"
            >
              {{ t(`team.roles.${member.role}`) }}
            </span>
          </td>

          <!-- Joined date -->
          <td class="px-3 py-3 text-slate-400">
            {{ formatDate(member.joined_at) }}
          </td>

          <!-- Actions -->
          <td v-if="isOwner" class="px-3 py-3 text-right">
            <div class="flex items-center justify-end">
              <OverflowMenu
                :ref="(el) => setMenuRef(member.id, el)"
                teleport
                menu-class="w-40"
                :disabled="!canShowRemoveButton(member) || saving"
                :aria-label="canShowRemoveButton(member)
                  ? t('team.members.row_actions')
                  : t('team.members.no_row_actions')"
              >
                <button
                  type="button"
                  :disabled="saving"
                  class="w-full text-left px-4 py-2 text-sm text-red-400 hover:text-red-300 hover:bg-slate-700/50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  @click="requestRemove(member.id)"
                >
                  {{ isSelf(member) ? t('team.members.leave_button') : t('team.members.remove_button') }}
                </button>
              </OverflowMenu>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
    </div>

    <!-- Remove / leave confirmation -->
    <ConfirmDialog
      :open="confirmingRemoveUserId !== null"
      :title="confirmingRemoveUserId === currentUserId ? t('team.members.confirm_leave_title') : t('team.members.confirm_remove_title')"
      :body="confirmingRemoveUserId === currentUserId ? t('team.members.confirm_leave_body') : t('team.members.confirm_remove_body')"
      :confirm-label="t('common.confirm')"
      :loading="saving"
      danger
      @update:open="cancelRemove"
      @confirm="confirmRemove"
    />

    <!-- Self-demote confirmation modal -->
    <div
      v-if="pendingRoleChange !== null"
      class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4"
      role="dialog"
      aria-modal="true"
      @click.self="pendingRoleChange = null"
    >
      <div class="bg-slate-800 rounded-2xl border border-slate-700 p-8 max-w-sm w-full shadow-xl">
        <div class="w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-4">
          <svg class="w-6 h-6 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <p class="text-sm text-slate-300 text-center mb-6">
          {{ t('team.members.confirm_demote_body') }}
        </p>
        <div class="flex gap-3">
          <button
            type="button"
            :disabled="saving"
            class="flex-1 bg-amber-500 hover:bg-amber-400 text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors disabled:opacity-50"
            @click="confirmRoleChange"
          >
            <span v-if="saving">{{ t('common.loading') }}</span>
            <span v-else>{{ t('common.confirm') }}</span>
          </button>
          <button
            type="button"
            class="flex-1 bg-slate-700 hover:bg-slate-600 text-slate-200 font-semibold rounded-lg px-5 py-2.5 text-sm border border-slate-600 transition-colors"
            @click="pendingRoleChange = null"
          >
            {{ t('common.cancel') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
